import React, { Suspense, useEffect } from 'react';
import { observer } from 'mobx-react';
import { Switch, withRouter } from 'react-router-dom';
import { IntlProvider } from 'react-intl';
import { ThemeProvider } from 'styled-components';
import LogRocket from 'logrocket';
import { toJS } from 'mobx';
import { createGlobalDataLayer, getSessionID } from '@ncl/modules/shared/globals/datalayer';
import FlagsProvider from '@ncl/modules/shared/components/FlagsProvider';
import {
  RequiredTermsModal,
  ConnectionErrorModal,
  DefaultLoadingSkeleton,
  OnboardingModal,
  JudicialNoRefundTermModal,
} from '@ncl/modules/shared/components';
import Alert from 'react-s-alert';
import { ZendeskWidget, LoadingPlaceholder, ErrorBoundary } from 'components-core';

import { registerLocales, saveCampaignCookie } from './utils';
import Application from './modules/core/containers/Application';
import StringsHelper from './strings/StringsHelper';
import { StoreProvider } from './rootStore';

import { routes as quote } from './modules/quote';
import { routes as policy } from './modules/policy';
import { routes as account } from './modules/account';
import { routes as panelRoutes } from './modules/panel';
import { routes as referral } from './modules/referral';
import lead from './modules/lead/routes';
import { routes as partnerships } from './modules/partnerships';
import { routes as page404Container } from './modules/404';

import 'react-s-alert/dist/s-alert-default.css';
import 'react-s-alert/dist/s-alert-css-effects/bouncyflip.css';
import './App.css';
import ScrollTop from './components/common/ScrollToTop';
import FlowStore from './modules/core/store/FlowStore';
import theme from './theme';
import AccountServices from './modules/account/services/AccountServices';
import AccountStore from './modules/account/store/AccountStore';
import * as AppServices from './App.services';
import useQuoteOrigin from './hooks/useQuoteOrigin';
import useExperiment from './hooks/useExperiment/useExperiment';

import pages from '@ncl/modules/pages';

import JuntoAnalyticsService from './services/analytics/JuntoAnalyticsService';
import NavigationChangedAdapter from './services/analytics/adapters/NavigationChangedAdapter';

const App = ({ location }) => {
  useQuoteOrigin();
  window.experimentId = process.env.REACT_APP_EXPERIMENT_ID;
  useExperiment(process.env.REACT_APP_EXPERIMENT_ID);

  useEffect(() => {
    AppServices.initializeGATracking();
    registerLocales();
    saveCampaignCookie();
    createGlobalDataLayer();
    setUpLogRocketIdentify();
    JuntoAnalyticsService.initializeJuntoAnalytics();
  }, []);

  useEffect(() => {
    JuntoAnalyticsService.configurePageNavigationAnalytics(NavigationChangedAdapter);
    JuntoAnalyticsService.pageChanged();
  }, [location]);

  useEffect(() => {
    const { pathname } = location;

    AppServices.updatePageTitle(pathname);
    AppServices.rdStationTracking(pathname);
  }, [location]);

  useEffect(() => {
    initializeUserData();
  }, []);

  function setUpLogRocketIdentify() {
    const { currentUser } = AccountStore;

    if (!currentUser.displayName) {
      const sessionID = getSessionID();
      LogRocket.identify(sessionID);
    }
  }

  async function initializeUserData() {
    await AccountServices.setAccountStoreValues();
    await AccountServices.validatePendingTerms();
  }

  return (
    <IntlProvider
      locale={FlowStore.modality || 'en'}
      messages={StringsHelper.getMessagesByModality()}
    >
      <FlagsProvider>
        <StoreProvider>
          <React.Fragment>
            <Alert stack={{ limit: 10 }} timeout={8000} />
            <ThemeProvider theme={theme}>
              <ErrorBoundary>
                <ConnectionErrorModal />
                <ScrollTop>
                  <Application id="app">
                    <Suspense fallback={<DefaultLoadingSkeleton />}>
                      <RequiredTermsModal />
                      <OnboardingModal />
                      <JudicialNoRefundTermModal />
                      <Switch>
                        {quote()}
                        {policy()}
                        {account()}
                        {partnerships()}
                        {panelRoutes()}
                        {pages()}
                        {lead()}
                        {referral()}
                        {page404Container({ showHeader: true })}
                      </Switch>
                    </Suspense>
                    <ZendeskWidget />
                  </Application>
                </ScrollTop>
              </ErrorBoundary>
            </ThemeProvider>
          </React.Fragment>
        </StoreProvider>
      </FlagsProvider>
    </IntlProvider>
  );
};

export default withRouter(observer(App));
